import { m as motion, useScroll } from 'framer-motion';
import { GetStaticProps, InferGetStaticPropsType } from 'next';
import { useEffect, useRef } from 'react';
import type { Thing } from 'schema-dts';
import { MAIN_FORUM_EDITION_YEAR, PAGES } from '@/consts';
import { Day } from '@/entities/day';
import { Direction } from '@/entities/direction';
import { Format } from '@/entities/format';
import { Page } from '@/entities/page';
import { Place } from '@/entities/place';
import { TeamPerson } from '@/entities/teamPerson';
import { getCommonPageProps } from '@/shared/api';
import { getDays } from '@/shared/api/days';
import { getDirections } from '@/shared/api/directions';
import { getEmployees } from '@/shared/api/employees';
import { getHistory } from '@/shared/api/history';
import { getPageDetail } from '@/shared/api/page-detail';
import { getFormats } from '@/shared/api/participation-formats';
import { getPeople } from '@/shared/api/people';
import { getPlaces } from '@/shared/api/places';
import { postVisitorsMaterials } from '@/shared/api/post-visitors-materials';
import { getPrivileges } from '@/shared/api/privileges';
import { ApiProgramRequest, AppProgramResponse, getProgram } from '@/shared/api/program';
import { getQuestions } from '@/shared/api/questions';
import { getSiteSettings } from '@/shared/api/site-settings';
import { getTicketFormats } from '@/shared/api/ticket-formats';
import { getTickets } from '@/shared/api/tickets';
import { useHeaderAnchorsEffect } from '@/shared/atoms/header-anchors';
import { useHeaderThemeValue } from '@/shared/atoms/header-theme';
import { PageDataState } from '@/shared/atoms/page-data';
import MapSection, { MapSectionProps } from '@/shared/components/MapBlock/MapSection';
import DefaultLayout from '@/shared/components/layout/DefaultLayout';
import BlockquoteSection, { BlockquoteSectionProps } from '@/shared/components/sections/BlockquoteSection';
import ExpoHeroSection, { ExpoHeroSectionRawProps } from '@/shared/components/sections/ExpoHeroSection';
import FaqSection, { FaqSectionProps } from '@/shared/components/sections/FaqSection';
import FormatsSection, { FormatsSectionRawProps } from '@/shared/components/sections/FormatsSection';
import HowItWasSection, { HowItWasSectionProps } from '@/shared/components/sections/HowItWasSection';
import KeyTopicsSection, { KeyTopicsSectionRawProps } from '@/shared/components/sections/KeyTopicsSection';
import ParticipantsListSection from '@/shared/components/sections/ParticipantsListSection';
import ProgramDetailSection from '@/shared/components/sections/ProgramDetailSection';
import { ProgramSectionProps } from '@/shared/components/sections/ProgramSection';
import QuestionsSection, { QuestionsSectionProps } from '@/shared/components/sections/QuestionsSection';
import SmallTextBlock, { SmallTextBlockRawProps } from '@/shared/components/sections/SmallTextBlock';
import SpeakersSection, { SpeakersSectionRawProps } from '@/shared/components/sections/SpeakersSection';
import {
    TariffsInfoSectionWithPopup,
    TariffsInfoSectionWithPopupProps,
} from '@/shared/components/sections/TariffsInfoSection';
import Breadcrumbs from '@/shared/components/shared/Breadcrumbs';
import DownloadPdfBtn from '@/shared/components/shared/DownloadPdfBtn/DownloadPdfBtn';
import JsonLd from '@/shared/components/shared/JsonLd/JsonLd';
import { ParticipantsListFormRawProps } from '@/shared/components/shared/ParticipantsListForm';
import ScrollTopButton from '@/shared/components/shared/ScrollTopButton';
import { PARTICIPATION_NAVIGATION } from '@/shared/consts/participation-nav';
import { Anchors, AppNavigation, CommonPageProps, NonUndefined } from '@/shared/lib/types';
import { tp } from '@/shared/lib/typograf';
import { generateBusinessEventJsonLd } from '@/shared/lib/utils/business-event-json-ld';
import { ddMmYyyyToIso8601Date, formatDate, parseEventDates } from '@/shared/lib/utils/dates';
import { filterFaqQuestionsVisibleInFaqSection } from '@/shared/lib/utils/faq-json-ld';
import { scrollingPageToAnchor } from '@/shared/lib/utils/scrollingPageToAnchor';
import { buildTariffContacts } from '@/shared/lib/utils/tariff-contacts';

const VISITORS_SECTION_IDS = {
    about: 'about',
    speakers: 'speakers',
    formats: 'formats',
    secondHowItWas: 'secondHowItWas',
    tarifs: 'tarifs',
    participantsList: 'participantsList',
    constructor: 'constructor',
    howItWas: 'howItWas',
    keyTopics: 'keyTopics',
    program: 'program',
    feedback: 'feedback',
    faq: 'faq',
} as const;

const ParticipationPage = ({
    pageData,
    breadcrumbs,
    bodyClass,
    navigation,
    expoHeroSectionProps,
    blockquoteSectionProps,
    speakersSectionProps,
    formatsSectionProps,
    programSectionProps,
    tariffsInfoSectionProps,
    participantsListSectionProps,
    howItWasSectionProps,
    secondHowItWasSectionProps,
    keyTopicsSectionProps,
    mapSectionProps,
    smallTextBlockProps,
    faqSectionProps,
    questionsSectionProps,
    contactPersonProps,
    anchors = [],
    sectionIds,
    footerData,
    meta,
    h1,
}: InferGetStaticPropsType<typeof getStaticProps>) => {
    const ref = useRef(null);
    const theme = useHeaderThemeValue();
    const { scrollYProgress } = useScroll({ target: ref, offset: ['start -30%', 'end end'] });

    useHeaderAnchorsEffect({ anchors, navigation, scrollYProgress });

    useEffect(() => {
        scrollingPageToAnchor();
    }, []);

    const eventDates = expoHeroSectionProps.date
        ? parseEventDates(expoHeroSectionProps.date, MAIN_FORUM_EDITION_YEAR)
        : null;

    return (
        <DefaultLayout currentPageId={PAGES.VISITORS} ref={ref} bodyClass={bodyClass}>
            <script
                type="application/json"
                className="js-page-data-json"
                dangerouslySetInnerHTML={{ __html: JSON.stringify(pageData) }}
            ></script>

            <ScrollTopButton theme={theme} scrollYProgress={scrollYProgress} universal />

            <section className="visitors__section">
                <Breadcrumbs className="visually-hidden" breadcrumbs={breadcrumbs} />
                <ExpoHeroSection id="top" expoHeroSectionProps={expoHeroSectionProps} />
                <BlockquoteSection {...blockquoteSectionProps} id={sectionIds.about} />

                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 1 }}
                    exit={{ opacity: 0 }}
                >
                    {formatsSectionProps.items && formatsSectionProps.items.length > 0 && (
                        <FormatsSection {...formatsSectionProps} id={sectionIds.formats} />
                    )}

                    {(speakersSectionProps.speakers?.length ?? 0) > 0 && (
                        <SpeakersSection id={sectionIds.speakers} speakersSectionProps={speakersSectionProps} />
                    )}

                    {(tariffsInfoSectionProps.tariffs?.length ?? 0) > 0 && (
                        <TariffsInfoSectionWithPopup {...tariffsInfoSectionProps} id={sectionIds.tarifs || 'tarifs'} />
                    )}

                    {secondHowItWasSectionProps.slider.images.length > 0 && (
                        <HowItWasSection
                            id={sectionIds.secondHowItWas}
                            howItWasSectionProps={secondHowItWasSectionProps}
                            theme="dark"
                            className="second-how-it-was-section"
                        />
                    )}

                    <ParticipantsListSection
                        id={sectionIds.participantsList}
                        participantsListSectionProps={{
                            ...participantsListSectionProps,
                            submitFn: postVisitorsMaterials,
                        }}
                    />

                    {/* {keyTopicsSectionProps.items && keyTopicsSectionProps.items.length > 0 && (
                        <KeyTopicsSection {...keyTopicsSectionProps} id={sectionIds.keyTopics} />
                    )} */}

                    {(programSectionProps.initialProgram.data?.items?.length ?? 0) > 0 && (
                        <div id={sectionIds.program} className="program-section-wrapper wrapper">
                            <div className="program-top">
                                {programSectionProps.title && (
                                    <h2 className="program-title h3">{programSectionProps.title}</h2>
                                )}

                                <div className="program-description-block__wrapper">
                                    <DownloadPdfBtn />
                                    {programSectionProps.subtitle && (
                                        <div className="program-description-block">
                                            <div className="program-description text-s">
                                                {tp(programSectionProps.subtitle)}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <ProgramDetailSection
                                programDetailSectionProps={{
                                    cards: programSectionProps.initialProgram.data?.items || [],
                                }}
                                days={programSectionProps.days}
                                directions={programSectionProps.directions}
                                places={programSectionProps.places}
                                formats={programSectionProps.formats}
                                initialParams={programSectionProps.initialParams}
                                initialResponse={programSectionProps.initialProgram}
                                pdfTitle="Главная тема Форума TNF 2026 <br/> Нефтегазовая отрасль будущего: энергия лидерства"
                            />
                        </div>
                    )}

                    <MapSection id={sectionIds.constructor} {...mapSectionProps} />
                    {/* <SmallTextBlock {...smallTextBlockProps} id={sectionIds.constructor} /> */}

                    {howItWasSectionProps.slider.images.length > 0 && (
                        <HowItWasSection
                            id={sectionIds.howItWas}
                            howItWasSectionProps={howItWasSectionProps}
                            theme="dark"
                        />
                    )}

                    <QuestionsSection
                        id={sectionIds.feedback}
                        questionsSectionProps={questionsSectionProps}
                        contactPersonProps={contactPersonProps}
                    />

                    {filterFaqQuestionsVisibleInFaqSection(faqSectionProps.questions).length > 0 && (
                        <FaqSection id={sectionIds.faq} faqSectionProps={faqSectionProps} />
                    )}
                </motion.div>
            </section>
            <JsonLd<Thing>
                inBody
                scriptKey="ld-json-visitors"
                data={generateBusinessEventJsonLd({
                    variant: 'expo',
                    name: expoHeroSectionProps.title || h1,
                    description: meta.description,
                    disambiguatingDescription: formatsSectionProps.text,
                    startDateIso: eventDates?.startDay ? ddMmYyyyToIso8601Date(eventDates.startDay) : null,
                    endDateIso: eventDates?.endDay ? ddMmYyyyToIso8601Date(eventDates.endDay) : null,
                    imageSrc: meta.ogImage,
                    offers: (tariffsInfoSectionProps.tariffs ?? []).map((tariff) => ({
                        name: tariff.title,
                        price: tariff.price,
                        url: tariff.button?.href,
                    })),
                    breadcrumbs,
                    faqQuestions: faqSectionProps.questions,
                    contacts: footerData,
                })}
            />
        </DefaultLayout>
    );
};

export default ParticipationPage;

type ParticipationPageProps = NonUndefined<
    CommonPageProps & {
        page?: Page;
        h1: string;
        pageData: PageDataState;
        faqSectionProps: FaqSectionProps;
        navigation: AppNavigation;
        expoHeroSectionProps: ExpoHeroSectionRawProps;
        blockquoteSectionProps: BlockquoteSectionProps;
        speakersSectionProps: SpeakersSectionRawProps;
        formatsSectionProps: FormatsSectionRawProps;
        tariffsInfoSectionProps: TariffsInfoSectionWithPopupProps;
        participantsListSectionProps: ParticipantsListFormRawProps;
        keyTopicsSectionProps: KeyTopicsSectionRawProps;
        mapSectionProps: MapSectionProps;
        smallTextBlockProps: SmallTextBlockRawProps;
        programSectionProps: ProgramSectionProps & {
            days: Day[];
            formats: Format[];
            places: Place[];
            directions: Direction[];
            initialParams: ApiProgramRequest;
            initialProgram: AppProgramResponse;
        };
        questionsSectionProps: QuestionsSectionProps;
        contactPersonProps: TeamPerson | null;
        howItWasSectionProps: HowItWasSectionProps;
        secondHowItWasSectionProps: HowItWasSectionProps;
        anchors?: Anchors;
        sectionIds: typeof VISITORS_SECTION_IDS;
    }
>;

export const getStaticProps: GetStaticProps<ParticipationPageProps> = async () => {
    const [pageResponse] = await Promise.all([getPageDetail({ params: { slug: 'visitors' } })]);
    const pageId = pageResponse.data?.id ?? PAGES.VISITORS;
    const daysResponse = await getDays({ params: { page: pageId } });

    const [
        commonPageProps,
        questionsResponse,
        employeesResponse,
        peopleResponse,
        privilegesResponse,
        ticketsResponse,
        siteSettings,
        placesResponse,
        directionsResponse,
        formatsResponse,
        [programDays, programParams, programDetailResponse],
    ] = await Promise.all([
        getCommonPageProps(pageId),
        getQuestions({ params: { page: pageId } }),
        getEmployees({ params: { page: pageId } }),
        getPeople({ params: { page: pageId } }),
        getPrivileges({ params: { page: pageId } }),
        getTickets({ params: { page: pageId } }),
        getSiteSettings({
            params: {
                keys: [
                    'visitors_first_screen_title',
                    'visitors_first_screen_caption',
                    'visitors_first_screen_date',
                    'visitors_first_screen_place',
                    'visitors_first_screen_program_button_text',
                    'visitors_first_screen_participate_button_text',
                    'visitors_first_screen_partners_title',
                    'visitors_first_screen_partner_images',
                    'visitors_first_screen_bg_video',
                    'visitors_first_screen_bg_image',

                    'visitors_about_quote',
                    'visitors_about_name',
                    'visitors_about_position',
                    'visitors_about_image',
                    'visitors_about_cards',

                    'visitors_key_formats_title',
                    'visitors_key_formats_description',
                    'visitors_key_formats_button_text',
                    'visitors_key_formats_tickets_caption',

                    'visitors_people_title',
                    'visitors_people_description',

                    'visitors_history_title',
                    'visitors_history_description',
                    'visitors_history_link_text',
                    'visitors_history_link',
                    'visitors_history_code',

                    'visitors_second_history_title',
                    'visitors_second_history_description',
                    'visitors_second_history_link_text',
                    'visitors_second_history_link',
                    'visitors_second_history_code',

                    'visitors_tariff_title',
                    'visitors_tariff_description',
                    'visitors_tariff_discount',
                    'visitors_tariff_button_text',
                    'visitors_tariff_document',
                    'visitors_tariff_disclaimer',
                    'visitors_tariff_discount_price_label',
                    'visitors_tariff_future_price_label',
                    'visitors_tariff_footnote_description',
                    'visitors_tariff_popup_title',
                    'visitors_tariff_contact_title',
                    'visitors_tariff_contact_description',
                    'visitors_tariff_contact_phone',
                    'visitors_tariff_contact_email',

                    'visitors_participants_list_form_title',
                    'visitors_participants_list_form_description',
                    'visitors_participants_list_form_partner_images',

                    'visitors_themes_title',
                    'visitors_themes_cards',

                    'visitors_scheme_title',
                    'visitors_scheme_description',
                    'visitors_scheme_button_text',

                    'visitors_program_title',
                    'visitors_program_description',

                    'visitors_form_title',
                    'visitors_form_description',

                    'visitors_faq_title',
                ],
            },
        }),
        getPlaces(),
        getDirections(),
        getFormats({ params: { page: pageId } }),
        getDays({ params: { page: pageId } }).then((daysResponseInner) => {
            const days = daysResponseInner.data || [];
            const initialParams: ApiProgramRequest = {
                for_visitors: 1,
                days: days.map((day) => day.id),
            };

            return Promise.all([
                days,
                initialParams,
                getProgram({
                    params: initialParams,
                }),
            ]);
        }),
    ]);

    const historyCode = siteSettings.data?.visitors_history_code;
    const secondHistoryCode = siteSettings.data?.visitors_second_history_code;

    const [historyResponse, secondHistoryResponse] = await Promise.all([
        getHistory({ params: { page: pageId, ...(historyCode ? { slug: historyCode } : {}) } }),
        secondHistoryCode ? getHistory({ params: { page: pageId, slug: secondHistoryCode } }) : null,
    ]);

    const tickets = ticketsResponse.data ?? [];
    const ticketFormatsResponse =
        tickets.length > 0 ? await getTicketFormats({ params: { tickets: tickets.map((ticket) => ticket.id) } }) : null;
    const ticketFormats = ticketFormatsResponse?.data ?? [];

    const formatsSectionProps = {
        title: siteSettings.data?.visitors_key_formats_title ?? '',
        text: siteSettings.data?.visitors_key_formats_description ?? '',
        buttonText: siteSettings.data?.visitors_key_formats_button_text ?? '',
        buttonHref: `#${VISITORS_SECTION_IDS.tarifs}`,
        tabsLabel: siteSettings.data?.visitors_key_formats_tickets_caption ?? '',
        items: ticketFormats.map((format) => ({
            id: String(format.id),
            title: format.name,
            text: format.description ?? '',
            href: format.url || null,
        })),
        tabs: tickets.map((ticket) => ({
            id: String(ticket.id),
            label: ticket.title,
            activeItemIds: ticketFormats
                .filter((format) => format.ticketIds.includes(ticket.id))
                .map((format) => String(format.id)),
        })),
    } satisfies FormatsSectionRawProps;

    const keyTopicsSectionProps = {
        title: siteSettings.data?.visitors_themes_title ?? '',
        items: (siteSettings.data?.visitors_themes_cards ?? []).map((card) => ({
            title: card.title,
            image: card.image ?? null,
        })),
    } satisfies KeyTopicsSectionRawProps;

    const speakers = peopleResponse.data ?? [];
    const tariffDisclaimer = siteSettings.data?.visitors_tariff_disclaimer ?? '';
    const tariffButtonText = siteSettings.data?.visitors_tariff_button_text ?? '';
    const tariffDocumentSrc = siteSettings.data?.visitors_tariff_document?.src;

    const tariffsInfoSectionProps = {
        title: siteSettings.data?.visitors_tariff_title ?? '',
        subtitle: siteSettings.data?.visitors_tariff_description ?? '',
        download: tariffDocumentSrc
            ? {
                  href: tariffDocumentSrc,
                  text: 'Скачать тарифы',
              }
            : null,
        popupTitle: siteSettings.data?.visitors_tariff_popup_title ?? '',
        allTariffFeatures: privilegesResponse.data ?? [],
        tariffs: tickets.map((ticket) => ({
            id: ticket.id,
            title: ticket.title,
            text: ticket.description ?? '',
            color: ticket.color || null,
            price: ticket.price ?? '',
            priceNote: tariffDisclaimer || null,
            button: {
                text: ticket.button?.text || tariffButtonText || null,
                href: ticket.button?.href || '/registration/',
                isExternal: ticket.button?.isExternal || false,
            },
            features: ticket.features ?? [],
            futurePrice: ticket.futurePrice ?? '',
        })),
        discounts: tickets
            .filter((ticket) => ticket.price)
            .map((ticket) => ({
                tariffId: ticket.id,
                price: ticket.price ?? '',
            })),
        discountPriceLabel: siteSettings.data?.visitors_tariff_discount_price_label || null,
        futurePriceLabel: siteSettings.data?.visitors_tariff_future_price_label || null,
        footnotes: siteSettings.data?.visitors_tariff_footnote_description ?? null,
        contacts: buildTariffContacts({
            title: siteSettings.data?.visitors_tariff_contact_title,
            description: siteSettings.data?.visitors_tariff_contact_description,
            phone: siteSettings.data?.visitors_tariff_contact_phone,
            email: siteSettings.data?.visitors_tariff_contact_email,
        }),
    } satisfies TariffsInfoSectionWithPopupProps;

    const bgVideo = siteSettings.data?.visitors_first_screen_bg_video;
    const heroMedia =
        bgVideo && bgVideo.length > 0 ? bgVideo : (siteSettings.data?.visitors_first_screen_bg_image ?? null);

    const visitorsAnchors: Anchors = [
        {
            id: VISITORS_SECTION_IDS.about,
            name: 'О форуме',
        },
        ...(speakers.length > 0
            ? [
                  {
                      id: VISITORS_SECTION_IDS.speakers,
                      name: 'Спикеры',
                  },
              ]
            : []),
        ...(formatsSectionProps.items && formatsSectionProps.items.length > 0
            ? [
                  {
                      id: VISITORS_SECTION_IDS.formats,
                      name: 'Ключевые форматы',
                  },
              ]
            : []),
        ...((secondHistoryResponse?.data?.images ?? []).length > 0
            ? [
                  {
                      id: VISITORS_SECTION_IDS.secondHowItWas,
                      name: siteSettings.data?.visitors_second_history_title || 'Как было в том году?',
                  },
              ]
            : []),
        ...(tickets.length > 0
            ? [
                  {
                      id: VISITORS_SECTION_IDS.tarifs,
                      name: 'Форматы',
                  },
              ]
            : []),
        {
            id: VISITORS_SECTION_IDS.constructor,
            name: 'Схема выставки TNF',
        },
        ...((historyResponse.data?.images ?? []).length > 0
            ? [
                  {
                      id: VISITORS_SECTION_IDS.howItWas,
                      name: 'Как было в том году?',
                  },
              ]
            : []),
        ...(keyTopicsSectionProps.items && keyTopicsSectionProps.items.length > 0
            ? [
                  {
                      id: VISITORS_SECTION_IDS.keyTopics,
                      name: 'Ключевые темы',
                  },
              ]
            : []),
        ...((programDetailResponse.data?.items?.length ?? 0) > 0 && programDays.length > 0
            ? [{ id: VISITORS_SECTION_IDS.program, name: 'Программа' }]
            : []),

        { id: VISITORS_SECTION_IDS.feedback, name: 'Обратная связь' },
        ...((questionsResponse.data ?? []).length > 0
            ? [
                  {
                      id: VISITORS_SECTION_IDS.faq,
                      name: 'Частые вопросы',
                  },
              ]
            : []),
    ];

    return {
        props: {
            ...commonPageProps,
            bodyClass: 'expo-page',
            meta: {
                ...commonPageProps.meta,
                ...(pageResponse.data?.meta ?? []),
            },
            breadcrumbs: [
                { text: 'TNF', href: '/' },
                { text: 'EXPO', href: '/participation/visitors/' },
            ],
            pageData: {
                pageId: pageResponse.data?.id ?? -1,
            },
            page: pageResponse.data,
            h1: pageResponse.data?.name ?? 'Участник EXPO',
            navigation: PARTICIPATION_NAVIGATION,
            // anchors: visitorsAnchors,
            sectionIds: VISITORS_SECTION_IDS,
            expoHeroSectionProps: {
                media: heroMedia,
                title: siteSettings.data?.visitors_first_screen_title ?? '',
                text: siteSettings.data?.visitors_first_screen_caption ?? '',
                date: siteSettings.data?.visitors_first_screen_date ?? '',
                location: siteSettings.data?.visitors_first_screen_place ?? '',
                programButtonText: siteSettings.data?.visitors_first_screen_program_button_text ?? '',
                participateButtonText: siteSettings.data?.visitors_first_screen_participate_button_text ?? '',
                participateButtonHref: `#${VISITORS_SECTION_IDS.tarifs}`,
                partnersTitle: siteSettings.data?.visitors_first_screen_partners_title ?? '',
                partners: siteSettings.data?.visitors_first_screen_partner_images ?? [],
            } satisfies ExpoHeroSectionRawProps,
            blockquoteSectionProps: {
                quote: siteSettings.data?.visitors_about_quote ?? '',
                authorName: siteSettings.data?.visitors_about_name ?? '',
                authorJob: siteSettings.data?.visitors_about_position ?? '',
                authorImage: siteSettings.data?.visitors_about_image ?? null,
                items: (siteSettings.data?.visitors_about_cards ?? []).map((card) => ({
                    title: card.title,
                    text: card.description ?? '',
                    img: card.image ?? null,
                })),
            } satisfies BlockquoteSectionProps,
            speakersSectionProps: {
                title: siteSettings.data?.visitors_people_title ?? '',
                text: siteSettings.data?.visitors_people_description ?? '',
                speakers,
            } satisfies SpeakersSectionRawProps,
            formatsSectionProps,
            keyTopicsSectionProps,
            tariffsInfoSectionProps,
            participantsListSectionProps: {
                title: siteSettings.data?.visitors_participants_list_form_title || '',
                description: siteSettings.data?.visitors_participants_list_form_description || '',
                video: [{ src: '/video/output-opt.mp4', type: 'video/mp4' }],
                poster: {
                    src: '/video/output-opt-poster.jpg',
                    width: 1920,
                    height: 1080,
                },
                logos: siteSettings.data?.visitors_participants_list_form_partner_images || [],
            },
            mapSectionProps: {
                title: siteSettings.data?.visitors_scheme_title ?? '',
                description: siteSettings.data?.visitors_scheme_description ?? '',
                buttonText: siteSettings.data?.visitors_scheme_button_text ?? '',
            },
            smallTextBlockProps: {
                title: 'Схема выставочных павильонов',
                text: 'Схема выставочных павильонов — на странице выставки',
                linkText: 'Выставка',
                linkHref: '/expo/#constructor',
                buttonText: 'Участвовать',
            },
            howItWasSectionProps: {
                slider: historyResponse.data ?? { images: [] },
                title: siteSettings.data?.visitors_history_title ?? '',
                description: siteSettings.data?.visitors_history_description ?? '',
                link: siteSettings.data?.visitors_history_link
                    ? {
                          href: siteSettings.data?.visitors_history_link,
                          name: siteSettings.data?.visitors_history_link_text ?? '',
                      }
                    : null,
            },
            secondHowItWasSectionProps: {
                slider: secondHistoryResponse?.data ?? { images: [] },
                title: siteSettings.data?.visitors_second_history_title ?? '',
                description: siteSettings.data?.visitors_second_history_description ?? '',
                link: siteSettings.data?.visitors_second_history_link
                    ? {
                          href: siteSettings.data?.visitors_second_history_link,
                          name: siteSettings.data?.visitors_second_history_link_text ?? '',
                      }
                    : null,
            },
            faqSectionProps: {
                title: siteSettings.data?.visitors_faq_title ?? '',
                questions: questionsResponse.data ?? [],
            },
            programSectionProps: {
                title: siteSettings.data?.visitors_program_title ?? '',
                subtitle: siteSettings.data?.visitors_program_description ?? '',
                cards: (daysResponse.data ?? []).map((day) => ({
                    date: formatDate(new Date(day.publishedAt), '.', false),
                    title: day.name,
                    description: day.description ?? '',
                    dayId: day.id,
                    publishedAt: day.publishedAt ?? '',
                    endedAt: day.endedAt ?? '',
                })),
                days: programDays,
                formats: formatsResponse.data || [],
                directions: directionsResponse.data || [],
                places: placesResponse.data || [],
                initialParams: programParams,
                initialProgram: programDetailResponse,
            },
            questionsSectionProps: {
                title: siteSettings.data?.visitors_form_title ?? '',
                description: siteSettings.data?.visitors_form_description ?? '',
            },
            contactPersonProps: employeesResponse.data?.[0] || null,
        } satisfies ParticipationPageProps,
        revalidate: 60,
    };
};
